import { useAuth } from '@agentnet/auth';
import { useIsFeatureEnabled } from '@agentnet/feature-flags';
import { createStyles, Grid, Link, makeStyles, Theme } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { FirmAddress, Office } from 'api/profile/types';
import { updateFincenCertifierAPI, updateFincenReporStatusAPI } from 'api/fincen-report-api';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useAutoSaveNavigation, useFincenAutoSave } from 'hooks/useAutoSave';
import { useNavigationLoading } from 'hooks/useNavigationLoading';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { certificationFormSchema } from 'utilities/validation/schemas/fincen-certifier-schema';
import { paymentSchema } from 'utilities/validation/schemas/fincen-payment-schema';
import propertyAddressSchema from 'utilities/validation/schemas/fincen-property-address-schema';
import reportingPartySchema from 'utilities/validation/schemas/fincen-reporting-party-schema';
import transfereeSchema from 'utilities/validation/schemas/fincen-transferee-schema';
import { transferorSchema } from 'utilities/validation/schemas/fincen-transferor-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import * as yup from 'yup';
import { isUSTerritory } from '@agentnet/utils';
import { AsyncStatusType } from '../constants';
import { useMenuContext } from '../ReportingLayout';
import { CertificationFormData } from '../types';
import CertifyReportContent from './CertifyReportContent';

const CertifyReportContainer: FC = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const history = useHistory();
  const { getAccessToken } = useAuth();
  const fileDataContext = useContext(FileDataContext) as FileDataContextInterface;
  const profileCtx = useContext<ProfileContextInterface | null>(ProfileContext);
  const { profile, userFirm, preferredOffice, currentOffice, latestProfile } = profileCtx ?? {};

  const { menuCollapsed } = useMenuContext();
  const isGenerateButtonEnabled = useIsFeatureEnabled(FeatureFlag.FINCEN_GENERATE_BUTTON_ENABLED);

  const isFileIdExist = fileId !== null && fileId !== undefined && fileId !== '';

  if (!isFileIdExist) {
    history.push('/files');
    return null;
  }

  const drawerWidth = menuCollapsed ? 73 : 309;
  const isNavigating = useNavigationLoading();

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      contentContainer: {
        paddingBottom: theme.spacing(12),
        display: 'flex',
        flexDirection: 'column',
      },
      mainContainer: {
        width: '100%',
        position: 'relative',
      },
      contentWrapper: {
        overflow: 'auto',
        paddingBottom: theme.spacing(12),
      },
      errorBlock: {
        position: 'fixed',
        bottom: theme.spacing(6.5),
        width: `calc(100% - ${drawerWidth}px)`,
        left: `${drawerWidth}px`,
        zIndex: theme.zIndex.appBar + 1,
        margin: 0,
        borderRadius: 0,
        boxSizing: 'border-box',
        maxWidth: '100%',
        overflow: 'hidden',
        marginLeft: '2px',
        marginBottom: '-2px',
        '& .MuiAlert-root': {
          width: '100%',
          maxWidth: '100%',
          borderRadius: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderLeft: 'none',
          borderRight: 'none',
          borderBottom: 'none',
          alignItems: 'flex-start',
          boxSizing: 'border-box',
          padding: theme.spacing(1.5, 3),
          backgroundColor: '#f6e3e2',
          color: '#5f2120',
        },
        '& .MuiAlert-icon': {
          color: '#ac1209',
        },
        '& .MuiAlertTitle-root': {
          color: '#5f2120',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: 1.5,
          letterSpacing: '0.15px',
        },
      },
      errorLink: {
        color: '#5f2120',
        cursor: 'pointer',
        textDecoration: 'underline',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: 1.43,
        letterSpacing: '0.15px',
        '&:hover, &:focus': {
          textDecoration: 'underline',
          outline: 'none',
          color: '#5f2120',
        },
      },
      errorListItem: {
        marginTop: theme.spacing(0.5),
      },
    }),
  );

  const classes = useStyles();

  const certificationStatus: AsyncStatusType = 'success';
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const {
    fincenReport,
    fincenReportStatus,
    setReportingPartyValidationErrors,
    clearReportingPartyValidationErrors,
    setPropertyAddressValidationErrors,
    clearPropertyAddressValidationErrors,
    setTransfereeValidationErrors,
    clearTransfereeValidationErrors,
    setTransferorValidationErrors,
    clearTransferorValidationErrors,
    setPaymentValidationErrors,
    updatePaymentInformation,
    paymentValidationErrors,
    clearPaymentValidationErrors,
    setCertifierValidationErrors,
    clearCertifierValidationErrors,
    updateCertifier,
  } = fincenReportCtx ?? {};

  interface ValidationResult {
    hasErrors: boolean;
    firstErrorForm: string | null;
    allErrors: {
      reportingParty: FieldValidationError[];
      property: FieldValidationError[];
      transferee: FieldValidationError[];
      transferor: FieldValidationError[];
      payment: FieldValidationError[];
      certifier: FieldValidationError[];
    };
  }

  const areValidationResultsEqual = (a: ValidationResult | null, b: ValidationResult | null): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.hasErrors !== b.hasErrors) return false;
    if (a.firstErrorForm !== b.firstErrorForm) return false;
    const keys: Array<keyof ValidationResult['allErrors']> = [
      'reportingParty',
      'property',
      'transferee',
      'transferor',
      'payment',
      'certifier',
    ];
    for (const k of keys) {
      const aArr = a.allErrors[k] || [];
      const bArr = b.allErrors[k] || [];
      if (aArr.length !== bArr.length) return false;
      for (let i = 0; i < aArr.length; i++) {
        if (aArr[i].field !== bArr[i].field || aArr[i].message !== bArr[i].message) return false;
      }
    }
    return true;
  };

  const validateFincenReport = async (): Promise<ValidationResult> => {
    const transfereeArraySchema = yup.array().of(transfereeSchema);
    const transferorArraySchema = yup.array().of(transferorSchema);
    const propertyAddressArraySchema = yup.array().of(propertyAddressSchema);

    const getValidatableData = (data: any, isArray: boolean) => {
      if (isArray) {
        return data && data.length > 0 ? data : [{}];
      }
      return data ?? {};
    };

    const fincenReportingPartyErrors = await doValidate(fincenReport?.reportingParty, reportingPartySchema);
    const fincenPropertyErrors = await doValidate(
      getValidatableData(fincenReport?.propertyAddresses, true),
      propertyAddressArraySchema,
    );
    const fincenTransfereeErrors = await doValidate(
      getValidatableData(fincenReport?.transferee, true),
      transfereeArraySchema,
    );
    const transferorToValidate = getValidatableData(fincenReport?.transferor, true);

    const fincenTransferorErrors = await doValidate(transferorToValidate, transferorArraySchema);

    const certifierDataToValidate = {
      ...fincenReport?.reportCertifier,
      ...formData,
    };
    const fincenCertifierErrors = await doValidate(certifierDataToValidate, certificationFormSchema);
    const fincenPaymentErrors = await doValidate(
      getValidatableData(fincenReport?.paymentDetails, false),
      paymentSchema,
    );

    // Set all validation errors in context
    if (setReportingPartyValidationErrors) {
      setReportingPartyValidationErrors(fincenReportingPartyErrors || []);
    }
    if (setPropertyAddressValidationErrors) {
      setPropertyAddressValidationErrors(fincenPropertyErrors || []);
    }
    if (setTransfereeValidationErrors) {
      setTransfereeValidationErrors(fincenTransfereeErrors || []);
    }
    if (setTransferorValidationErrors) {
      setTransferorValidationErrors(fincenTransferorErrors || []);
    }
    if (setPaymentValidationErrors) {
      setPaymentValidationErrors(fincenPaymentErrors || []);
    }
    if (setCertifierValidationErrors) {
      setCertifierValidationErrors(fincenCertifierErrors || []);
    }
    const allErrors = {
      reportingParty: fincenReportingPartyErrors || [],
      property: fincenPropertyErrors || [],
      transferee: fincenTransfereeErrors || [],
      transferor: fincenTransferorErrors || [],
      payment: fincenPaymentErrors || [],
      certifier: fincenCertifierErrors || [],
    };

    // Determine which form has the first error (following the form flow)
    let firstErrorForm: string | null = null;
    if (allErrors.reportingParty.length > 0) {
      firstErrorForm = 'report-party';
    } else if (allErrors.property.length > 0) {
      firstErrorForm = 'property-info';
    } else if (allErrors.transferor.length > 0) {
      firstErrorForm = 'transferor-info';
    } else if (allErrors.transferee.length > 0) {
      firstErrorForm = 'transferee-info';
    } else if (allErrors.payment.length > 0) {
      firstErrorForm = 'payment-info';
    } else if (allErrors.certifier.length > 0) {
      firstErrorForm = 'certify-generate';
    }

    const hasErrors = Object.values(allErrors).some((errors) => errors.length > 0);

    return {
      hasErrors,
      firstErrorForm,
      allErrors,
    };
  };
  const [formData, setFormData] = useState<CertificationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    certification: false,
    officeName: '',
    firmTaxId: '',
    officeAddress1: '',
    officeAddress2: '',
    officeCity: '',
    officeCountry: 'US',
    officeState: '',
    officeZip: '',
    officePhone: '',
    transmitterControlCode: '',
    sdtmUserId: '',
    autoFilledFields: [],
  });

  const [originalProfileValues, setOriginalProfileValues] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
  }>({});

  const handleAutoSave = useCallback(async (): Promise<void> => {
    if (!fincenReport) return;
    // Disable auto-save when report is in status 9302 (generated)
    if (fincenReport.reportStatusCdId === 9302) return;
    const token = await getAccessToken();
    await updateFincenCertifierAPI(String(fincenReport.fincenReportId || 0), formData, token);
    if (updateCertifier) {
      updateCertifier(formData);
    }
  }, [formData, fincenReport, getAccessToken, updateCertifier]);

  useAutoSaveNavigation('certify-report', handleAutoSave, fileId, fincenReport?.reportStatusCdId !== 9302);

  const { triggerSave, isSaving } = useFincenAutoSave({
    data: formData,
    updateFunction: updateCertifier,
    apiSaveFunction: updateFincenCertifierAPI,
    reportId: fincenReport?.fincenReportId,
    hasContent: (data) => !!(data.firstName || data.lastName || data.email),
    enabled: fincenReport?.reportStatusCdId !== 9302,
    errorContext: 'certification information',
  });

  useEffect(() => {
    const certifierData = fincenReport?.reportCertifier;

    const hasSavedData =
      certifierData &&
      (certifierData.firstName?.trim() || certifierData.lastName?.trim() || certifierData.email?.trim());

    // Get office information from profile - find the firm matching userFirm.firmId, then find default office
    let selectedOffice: Office | null = null;
    let officeAddress: FirmAddress | null | undefined = null;
    let latestOfficePhone: string | null = null;

    if (profile?.firms && userFirm?.firmId) {
      // Find the firm that matches userFirm.firmId
      const matchingFirm = profile.firms.find((firm) => String(firm.firmId) === String(userFirm.firmId));

      if (matchingFirm?.offices) {
        // Find the default office in that firm
        selectedOffice = matchingFirm.offices.find((office) => office.isDefault === true) || matchingFirm.offices[0];
        officeAddress = selectedOffice?.address;
      }
    }

    // Get phone number from latestProfile (user/me endpoint) which has PhoneNumber field
    if (latestProfile?.FirmAccess && userFirm?.firmId && selectedOffice) {
      const officeId = selectedOffice.officeId;
      const matchingFirm = latestProfile.FirmAccess.find(
        (firm: any) => String(firm.FirmId) === String(userFirm.firmId),
      );
      if (matchingFirm?.Offices) {
        const matchingOffice = matchingFirm.Offices.find((office: any) => office.OfficeId === officeId);
        // Format phone number as XXX-XXX-XXXX
        const phoneNumber = matchingOffice?.PhoneNumber;
        if (phoneNumber) {
          const digits = phoneNumber.replace(/\D/g, '');
          if (digits.length === 10) {
            latestOfficePhone = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
          } else {
            latestOfficePhone = phoneNumber;
          }
        }
      }
    }

    if (hasSavedData && profile) {
      const autoFilledFieldsList: string[] = [];

      if (certifierData.firstName === profile.firstName && profile.firstName?.trim()) {
        autoFilledFieldsList.push('firstName');
      }
      if (certifierData.lastName === profile.lastName && profile.lastName?.trim()) {
        autoFilledFieldsList.push('lastName');
      }
      if (certifierData.email === profile.emailAddress && profile.emailAddress?.trim()) {
        autoFilledFieldsList.push('email');
      }
      if (certifierData.confirmEmail === profile.emailAddress && profile.emailAddress?.trim()) {
        autoFilledFieldsList.push('confirmEmail');
      }

      // Auto-fill office fields from profile data if not saved
      const officeName = certifierData.officeName ?? selectedOffice?.name ?? '';
      const firmTaxId = certifierData.firmTaxId ?? '';
      const officeAddress1 = certifierData.officeAddress1 ?? officeAddress?.address1 ?? '';
      const officeAddress2 = certifierData.officeAddress2 ?? officeAddress?.address2 ?? '';
      const officeCity = certifierData.officeCity ?? officeAddress?.city ?? '';
      const officeCountry = certifierData.officeCountry ?? 'US';
      const officeState = certifierData.officeState ?? officeAddress?.state ?? '';
      const officeZip = certifierData.officeZip ?? officeAddress?.postalCode ?? '';
      const officePhone = certifierData.officePhone ?? latestOfficePhone ?? '';

      // Mark office fields as auto-filled if they match the profile data
      if (officeName && officeName === selectedOffice?.name) {
        autoFilledFieldsList.push('officeName');
      }
      if (officeAddress1 && officeAddress1 === officeAddress?.address1) {
        autoFilledFieldsList.push('officeAddress1');
      }
      if (officeAddress2 && officeAddress2 === officeAddress?.address2) {
        autoFilledFieldsList.push('officeAddress2');
      }
      if (officeCity && officeCity === officeAddress?.city) {
        autoFilledFieldsList.push('officeCity');
      }
      if (officeCountry && officeCountry === 'US') {
        autoFilledFieldsList.push('officeCountry');
      }
      if (officeState && officeState === officeAddress?.state) {
        autoFilledFieldsList.push('officeState');
      }
      if (officeZip && officeZip === officeAddress?.postalCode) {
        autoFilledFieldsList.push('officeZip');
      }
      if (officePhone && officePhone === latestOfficePhone) {
        autoFilledFieldsList.push('officePhone');
      }

      setFormData({
        ...certifierData,
        certification: fincenReport?.reportStatusCdId === 9302 ? true : certifierData.certification ?? false,
        officeName,
        firmTaxId,
        officeAddress1,
        officeAddress2,
        officeCity,
        officeCountry,
        officeState,
        officeZip,
        officePhone,
        transmitterControlCode: certifierData.transmitterControlCode ?? '',
        sdtmUserId: certifierData.sdtmUserId ?? '',
        autoFilledFields: autoFilledFieldsList,
      });

      setOriginalProfileValues({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.emailAddress || '',
      });
    } else if (profile) {
      const autoFilledFieldsList: string[] = [];

      if (profile.firstName?.trim()) {
        autoFilledFieldsList.push('firstName');
      }
      if (profile.lastName?.trim()) {
        autoFilledFieldsList.push('lastName');
      }
      if (profile.emailAddress?.trim()) {
        autoFilledFieldsList.push('email');
        autoFilledFieldsList.push('confirmEmail');
      }

      // Auto-fill office fields from profile data
      const officeName = selectedOffice?.name || '';
      const officeAddress1 = officeAddress?.address1 || '';
      const officeAddress2 = officeAddress?.address2 || '';
      const officeCity = officeAddress?.city || '';
      const officeCountry = 'US';
      const officeState = officeAddress?.state || '';
      const officeZip = officeAddress?.postalCode || '';
      const officePhone = latestOfficePhone || '';

      // Mark office fields as auto-filled
      if (officeName) {
        autoFilledFieldsList.push('officeName');
      }
      if (officeAddress1) {
        autoFilledFieldsList.push('officeAddress1');
      }
      if (officeAddress2) {
        autoFilledFieldsList.push('officeAddress2');
      }
      if (officeCity) {
        autoFilledFieldsList.push('officeCity');
      }
      if (officeCountry) {
        autoFilledFieldsList.push('officeCountry');
      }
      if (officeState) {
        autoFilledFieldsList.push('officeState');
      }
      if (officeZip) {
        autoFilledFieldsList.push('officeZip');
      }
      if (officePhone) {
        autoFilledFieldsList.push('officePhone');
      }

      setOriginalProfileValues({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.emailAddress || '',
      });

      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.emailAddress || '',
        confirmEmail: profile.emailAddress || '',
        certification: fincenReport?.reportStatusCdId === 9302 ? true : false,
        officeName,
        firmTaxId: '',
        officeAddress1,
        officeAddress2,
        officeCity,
        officeCountry,
        officeState,
        officeZip,
        officePhone,
        transmitterControlCode: '',
        sdtmUserId: '',
        autoFilledFields: autoFilledFieldsList,
      });
    }
  }, [fincenReport?.reportCertifier, profile, userFirm, preferredOffice, currentOffice, latestProfile]);

  const handleFormChange = (field: string, value: string | boolean) => {
    // Use functional state update to avoid race conditions when multiple fields update rapidly (e.g., Google autocomplete)
    setFormData((prevFormData) => {
      const currentAutoFilledFields = prevFormData.autoFilledFields || [];
      let updatedAutoFilledFields = [...currentAutoFilledFields];

      // Get office information from profile - find the firm matching userFirm.firmId, then find default office
      let selectedOffice: Office | null = null;
      let officeAddress: FirmAddress | null | undefined = null;
      let latestOfficePhone: string | null = null;

      if (profile?.firms && userFirm?.firmId) {
        // Find the firm that matches userFirm.firmId
        const matchingFirm = profile.firms.find((firm) => String(firm.firmId) === String(userFirm.firmId));

        if (matchingFirm?.offices) {
          // Find the default office in that firm
          selectedOffice = matchingFirm.offices.find((office) => office.isDefault === true) || matchingFirm.offices[0];
          officeAddress = selectedOffice?.address;
        }
      }

      // Get phone number from latestProfile (user/me endpoint) which has PhoneNumber field
      if (latestProfile?.FirmAccess && userFirm?.firmId && selectedOffice) {
        const officeId = selectedOffice.officeId;
        const matchingFirm = latestProfile.FirmAccess.find(
          (firm: any) => String(firm.FirmId) === String(userFirm.firmId),
        );
        if (matchingFirm?.Offices) {
          const matchingOffice = matchingFirm.Offices.find((office: any) => office.OfficeId === officeId);
          // Format phone number as XXX-XXX-XXXX
          const phoneNumber = matchingOffice?.PhoneNumber;
          if (phoneNumber) {
            const digits = phoneNumber.replace(/\D/g, '');
            if (digits.length === 10) {
              latestOfficePhone = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
            } else {
              latestOfficePhone = phoneNumber;
            }
          }
        }
      }

      // Handle country change to auto-set state for US Territories
      const updatedFormData = { ...prevFormData, [field]: value };
      if (field === 'officeCountry') {
        const countryUpper = (value as string)?.toUpperCase();
        // Auto-set state to territory value for US Territories, reset for other countries
        if (isUSTerritory(countryUpper)) {
          updatedFormData.officeState = countryUpper;
        } else {
          updatedFormData.officeState = '';
        }
      }

      // Compare with original profile value to determine if icon should be hidden
      if (field === 'firstName' || field === 'lastName' || field === 'email') {
        const originalValue = originalProfileValues[field as keyof typeof originalProfileValues] || '';
        const hasChanged = value !== originalValue;

        if (hasChanged) {
          // Remove from auto-filled if value differs from original
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          // Add back to auto-filled if value matches original
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'confirmEmail') {
        // For confirmEmail, compare with original email value
        const originalEmailValue = originalProfileValues.email || '';
        const hasChanged = value !== originalEmailValue;

        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'officeName') {
        const originalValue = selectedOffice?.name || '';
        const hasChanged = value !== originalValue;
        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'officeAddress1') {
        const originalValue = officeAddress?.address1 || '';
        const hasChanged = value !== originalValue;
        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'officeAddress2') {
        const originalValue = officeAddress?.address2 || '';
        const hasChanged = value !== originalValue;
        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'officeCity') {
        const originalValue = officeAddress?.city || '';
        const hasChanged = value !== originalValue;
        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'officeState') {
        const originalValue = officeAddress?.state || '';
        const hasChanged = value !== originalValue;
        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'officeZip') {
        const originalValue = officeAddress?.postalCode || '';
        const hasChanged = value !== originalValue;
        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'officeCountry') {
        const originalValue = 'US';
        const hasChanged = value !== originalValue;
        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      } else if (field === 'officePhone') {
        const originalValue = latestOfficePhone || '';
        const hasChanged = value !== originalValue;
        if (hasChanged) {
          updatedAutoFilledFields = updatedAutoFilledFields.filter((f: string) => f !== field);
        } else if (!updatedAutoFilledFields.includes(field)) {
          updatedAutoFilledFields.push(field);
        }
      }

      updatedFormData.autoFilledFields = updatedAutoFilledFields;

      if (updateCertifier) {
        updateCertifier(updatedFormData);
      }

      return updatedFormData;
    });
  };

  // Batch update for Google autocomplete - updates multiple fields at once to avoid race conditions
  const handleBatchFormChange = (updates: Partial<CertificationFormData>) => {
    setFormData((prevFormData) => {
      const updatedFormData = { ...prevFormData, ...updates };

      // Handle country change to auto-set state for US Territories
      if ('officeCountry' in updates) {
        const countryUpper = (updates.officeCountry as string)?.toUpperCase();
        if (isUSTerritory(countryUpper) && !('officeState' in updates)) {
          updatedFormData.officeState = countryUpper;
        }
      }

      if (updateCertifier) {
        updateCertifier(updatedFormData);
      }

      return updatedFormData;
    });
  };

  const handleSaveReport = async () => {
    setFormSubmitted(true);
  };

  const handleBack = () => {
    setValidationResult(null);
    history.push(`/files/${fileId}/fincen-reporting/payment-info`);
  };

  const handleFormSubmissionComplete = () => {
    setFormSubmitted(false);
  };
  const clearAllValidationErrors = () => {
    if (clearReportingPartyValidationErrors) clearReportingPartyValidationErrors();
    if (clearPropertyAddressValidationErrors) clearPropertyAddressValidationErrors();
    if (clearTransfereeValidationErrors) clearTransfereeValidationErrors();
    if (clearTransferorValidationErrors) clearTransferorValidationErrors();
    if (clearPaymentValidationErrors) clearPaymentValidationErrors();
    if (clearCertifierValidationErrors) clearCertifierValidationErrors();
  };

  const getFormDisplayInfo = (formKey: string) => {
    const formMap: { [key: string]: { name: string; path: string } } = {
      reportingParty: { name: 'Reporting Party Information', path: 'report-party' },
      property: { name: 'Property Information', path: 'property-info' },
      transferor: { name: 'Transferor Information', path: 'transferor-info' },
      transferee: { name: 'Transferee Information', path: 'transferee-info' },
      payment: { name: 'Payment Information', path: 'payment-info' },
      certifier: { name: 'Certifier Information', path: 'certify-generate' },
    };
    return formMap[formKey] || { name: formKey, path: formKey };
  };

  const handleErrorLinkClick = (formPath: string) => {
    history.push(`/files/${fileId}/fincen-reporting/${formPath}`);
  };

  // Auto-validation effect - show validation errors when auto-validation is triggered
  useEffect(() => {
    const { autoValidationTriggered } = fincenReportCtx ?? {};
    if (autoValidationTriggered && !validationResult) {
      // Trigger validation to show errors from auto-validation
      validateFincenReport().then((result) => {
        if (result.hasErrors) {
          setValidationResult(result);
        }
      });
    }
  }, [fincenReportCtx?.autoValidationTriggered, validationResult]);

  // Keep the Missing Required Information panel in sync purely from context errors
  useEffect(() => {
    const ctx = fincenReportCtx;
    if (!ctx) return;

    const certifierErrorsGated =
      (formSubmitted || ctx.autoValidationTriggered) && ctx.certifierValidationErrors
        ? ctx.certifierValidationErrors
        : [];

    const allErrors = {
      reportingParty: ctx.reportingPartyValidationErrors || [],
      property: ctx.propertyAddressValidationErrors || [],
      transferee: ctx.transfereeValidationErrors || [],
      transferor: ctx.transferorValidationErrors || [],
      payment: ctx.paymentValidationErrors || [],
      certifier: certifierErrorsGated,
    };

    const hasErrors = Object.values(allErrors).some((errs) => errs.length > 0);

    if (!hasErrors) {
      if (validationResult !== null) setValidationResult(null);
      return;
    }

    let firstErrorForm: string | null = null;
    if (allErrors.reportingParty.length > 0) firstErrorForm = 'report-party';
    else if (allErrors.property.length > 0) firstErrorForm = 'property-info';
    else if (allErrors.transferor.length > 0) firstErrorForm = 'transferor-info';
    else if (allErrors.transferee.length > 0) firstErrorForm = 'transferee-info';
    else if (allErrors.payment.length > 0) firstErrorForm = 'payment-info';
    else if (allErrors.certifier.length > 0) firstErrorForm = 'certify-generate';

    const nextResult: ValidationResult = { hasErrors, firstErrorForm, allErrors };
    if (!areValidationResultsEqual(validationResult, nextResult)) {
      setValidationResult(nextResult);
    }
  }, [
    fincenReportCtx?.reportingPartyValidationErrors,
    fincenReportCtx?.propertyAddressValidationErrors,
    fincenReportCtx?.transferorValidationErrors,
    fincenReportCtx?.transfereeValidationErrors,
    fincenReportCtx?.paymentValidationErrors,
    fincenReportCtx?.certifierValidationErrors,
    fincenReportCtx?.autoValidationTriggered,
    formSubmitted,
    validationResult,
  ]);

  const handleGenerateReport = async () => {
    setFormSubmitted(true);
    fincenReportCtx?.setGenerateButtonClicked?.(true);

    const result = await validateFincenReport();
    setValidationResult(result);

    const token = await getAccessToken();

    if (!result.hasErrors) {
      clearAllValidationErrors();

      // Call PUT API with ReportStatusCdId 9302 for success
      try {
        await updateFincenReporStatusAPI(
          fileId,
          {
            FincenReportId: fincenReport?.fincenReportId || 0,
            ReportStatusCdId: 9302,
          },
          token,
        );

        // Reload the fincen report to get the updated status
        await fincenReportCtx?.reloadFincenReport?.(fileId);

        // Set showSummary to true after the status is updated
        fincenReportCtx?.setShowSummary?.(true);
      } catch (error) {
        console.error('Error updating report status:', error);
      }

      history.push(`/files/${fileId}/fincen-reporting/summary`);
    } else {
      setFormSubmitted(false);

      // Call PUT API with ReportStatusCdId 9301 for validation errors
      try {
        await updateFincenReporStatusAPI(
          fileId,
          {
            FincenReportId: fincenReport?.fincenReportId || 0,
            ReportStatusCdId: 9301,
          },
          token,
        );
      } catch (error) {
        console.error('Error updating report status:', error);
      }
    }
  };
  return (
    <div data-testid="fincen-certify-container" className={classes.mainContainer}>
      <LoadingSpinner
        status={formSubmitted || isNavigating || fincenReportStatus === 'pending' ? 'pending' : 'success'}
        variant="linear"
      />
      <Grid container spacing={0}>
        <Grid item xs className={classes.contentWrapper}>
          <div className={classes.contentContainer}>
            <CertifyReportContent
              status={certificationStatus as AsyncStatusType}
              fileData={fileDataContext?.fileData}
              menuCollapsed={menuCollapsed}
              formSubmitted={formSubmitted}
              formData={formData}
              onFormChange={handleFormChange}
              onBatchFormChange={handleBatchFormChange}
              onFormSubmissionComplete={handleFormSubmissionComplete}
              isReadOnly={fincenReport?.reportStatusCdId === 9302}
            />
          </div>
        </Grid>
      </Grid>

      {validationResult && validationResult.hasErrors && fincenReportCtx?.generateButtonClicked && (
        <div className={classes.errorBlock}>
          <Alert severity="error">
            <AlertTitle>Missing Required Information:</AlertTitle>
            {Object.entries(validationResult.allErrors)
              .filter(([key, errors]) => errors.length > 0)
              .map(([key]) => (
                <div key={key} className={classes.errorListItem}>
                  •{' '}
                  <Link
                    className={classes.errorLink}
                    onClick={() => handleErrorLinkClick(getFormDisplayInfo(key).path)}
                  >
                    {getFormDisplayInfo(key).name}
                  </Link>
                </div>
              ))}
          </Alert>
        </div>
      )}
      <BottomAppBar
        cancelLabel="Back"
        onCancel={handleBack}
        cancelDataQa="fincen-certify-back-button"
        secondaryActionLabel="Save Report"
        onSecondaryClick={handleSaveReport}
        secondaryActionDataQa="fincen-certify-save-button"
        primaryActionLabel="Generate Report"
        onPrimaryClick={handleGenerateReport}
        primaryActionDataQa="fincen-certify-generate-button"
        disablePrimary={fincenReport?.reportStatusCdId === 9302 || !isGenerateButtonEnabled}
        disableSecondary={fincenReport?.reportStatusCdId === 9302}
      />
    </div>
  );
};
export default CertifyReportContainer;
